'use client';

import { useMemo, useState, type ReactNode } from 'react';
import { AdminEmptyState } from '@/components/ui/admin/AdminEmptyState';

export type TableDensity = 'comfortable' | 'compact';

type RenderColumn<T> = {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  sticky?: boolean;
  headerClassName?: string;
  cellClassName?: string;
  defaultVisible?: boolean;
};

type CellColumn<T> = {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  sticky?: boolean;
  headerClassName?: string;
  cellClassName?: string;
  defaultVisible?: boolean;
};

export type AdminResponsiveTableColumn<T> = RenderColumn<T> | CellColumn<T>;

type PaginationProps = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
};

type Props<T extends Record<string, unknown>> = {
  columns: AdminResponsiveTableColumn<T>[];
  rows: T[];
  rowKey?: (row: T) => string;
  loading?: boolean;
  empty?: ReactNode;
  emptyMessage?: string;
  minWidth?: string;
  density?: TableDensity;
  defaultDensity?: TableDensity;
  onDensityChange?: (density: TableDensity) => void;
  enableDensityToggle?: boolean;
  enableColumnVisibility?: boolean;
  bulkActions?: ReactNode;
  toolbar?: ReactNode;
  pagination?: PaginationProps;
  onHeaderClick?: (columnKey: string) => void;
};

function columnKey<T>(col: AdminResponsiveTableColumn<T>): string {
  return 'render' in col ? col.key : col.id;
}

function renderCell<T>(col: AdminResponsiveTableColumn<T>, row: T): ReactNode {
  return 'render' in col ? col.render(row) : col.cell(row);
}

function densityPadding(density: TableDensity): string {
  return density === 'compact' ? 'px-3 py-2' : 'px-4 py-3';
}

export function AdminResponsiveTable<T extends Record<string, unknown>>({
  columns,
  rows,
  rowKey,
  loading = false,
  empty,
  emptyMessage = 'No records found.',
  minWidth = '960px',
  density: controlledDensity,
  defaultDensity = 'comfortable',
  onDensityChange,
  enableDensityToggle = false,
  enableColumnVisibility = false,
  bulkActions,
  toolbar,
  pagination,
  onHeaderClick,
}: Props<T>) {
  const [internalDensity, setInternalDensity] = useState<TableDensity>(defaultDensity);
  const density = controlledDensity ?? internalDensity;

  const initialVisibility = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const col of columns) {
      const key = columnKey(col);
      map[key] = col.defaultVisible !== false;
    }
    return map;
  }, [columns]);

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(initialVisibility);

  const activeColumns = columns.filter((col) => visibleColumns[columnKey(col)] !== false);
  const cellPad = densityPadding(density);

  const resolveKey = (row: T, index: number) => {
    if (rowKey) return rowKey(row);
    const id = row.id;
    return typeof id === 'string' || typeof id === 'number' ? String(id) : `row-${index}`;
  };

  const setDensity = (next: TableDensity) => {
    if (!controlledDensity) setInternalDensity(next);
    onDensityChange?.(next);
  };

  const showToolbar = Boolean(toolbar || bulkActions || enableDensityToggle || enableColumnVisibility);

  if (loading) {
    return (
      <div className="rounded-2xl border overflow-hidden">
        <div className="animate-pulse space-y-2 p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-10 rounded-lg bg-nature-bg/80" />
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    if (empty) return <>{empty}</>;
    return (
      <AdminEmptyState title="No records" description={typeof emptyMessage === 'string' ? emptyMessage : 'No records found.'} />
    );
  }

  const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : 1;

  return (
    <div className="space-y-3">
      {showToolbar ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">{bulkActions}{toolbar}</div>
          <div className="flex flex-wrap items-center gap-2">
            {enableDensityToggle ? (
              <div className="inline-flex rounded-lg border bg-bg p-0.5 text-xs">
                <button
                  type="button"
                  className={`rounded-md px-2.5 py-1 ${density === 'comfortable' ? 'bg-nature-bg font-semibold' : ''}`}
                  onClick={() => setDensity('comfortable')}
                >
                  Comfortable
                </button>
                <button
                  type="button"
                  className={`rounded-md px-2.5 py-1 ${density === 'compact' ? 'bg-nature-bg font-semibold' : ''}`}
                  onClick={() => setDensity('compact')}
                >
                  Compact
                </button>
              </div>
            ) : null}
            {enableColumnVisibility ? (
              <details className="relative">
                <summary className="cursor-pointer list-none rounded-lg border bg-bg px-3 py-1.5 text-xs font-medium">
                  Columns
                </summary>
                <div className="absolute right-0 z-20 mt-1 min-w-[180px] rounded-lg border bg-bg p-2 shadow-lg">
                  {columns.map((col) => {
                    const key = columnKey(col);
                    return (
                      <label key={key} className="flex items-center gap-2 px-2 py-1 text-xs">
                        <input
                          type="checkbox"
                          checked={visibleColumns[key] !== false}
                          onChange={() =>
                            setVisibleColumns((prev) => ({
                              ...prev,
                              [key]: !(prev[key] !== false),
                            }))
                          }
                        />
                        <span>{typeof col.header === 'string' ? col.header : key}</span>
                      </label>
                    );
                  })}
                </div>
              </details>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm" style={{ minWidth }}>
            <thead className="bg-nature-bg/90 text-left text-nature-muted sticky top-0 z-10 backdrop-blur-sm shadow-sm">
              <tr>
                {activeColumns.map((col) => {
                  const key = columnKey(col);
                  const clickable = Boolean(onHeaderClick && key !== 'select' && key !== 'actions');
                  return (
                    <th
                      key={key}
                      className={`${cellPad} font-semibold whitespace-nowrap ${col.headerClassName ?? ''} ${
                        col.sticky ? 'sticky right-0 bg-nature-bg/95 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]' : ''
                      } ${clickable ? 'cursor-pointer select-none hover:text-soot' : ''}`}
                      onClick={clickable ? () => onHeaderClick?.(key) : undefined}
                    >
                      {col.header}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={resolveKey(row, index)} className="border-t hover:bg-nature-bg/30">
                  {activeColumns.map((col) => {
                    const key = columnKey(col);
                    return (
                      <td
                        key={key}
                        className={`${cellPad} align-middle ${col.cellClassName ?? ''} ${
                          col.sticky ? 'sticky right-0 bg-bg shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.06)]' : ''
                        }`}
                      >
                        {renderCell(col, row)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-nature-muted">
          <span>
            Page {pagination.page} of {totalPages} · {pagination.total} total
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
              onClick={() => pagination.onPageChange(pagination.page - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= totalPages}
              className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
              onClick={() => pagination.onPageChange(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
